/** Alerts & exceptions + data quality tab - operational alerts (target breach, sudden
 * increases, repeated waste, high-value waste, missing reasons, unactioned expiry, supplier
 * quality issues, unusual adjustments, excessive plate waste, missing submissions, overdue
 * corrective actions) and data quality metrics (submission rates, unclassified waste,
 * estimated vs measured, inconsistent units, duplicates, confidence score). */

import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../components/ui/Section';
import { DATA_QUALITY, WASTE_ALERTS } from '../data';
import { StatusChip } from './StatusChip';

const AlertsDataQualityTab = () => {
  const { t } = useTranslation('common');
  const sortedAlerts = [...WASTE_ALERTS].sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'critical' ? -1 : 1));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textMD"
            fontWeight={600}
            sx={{ mb: 3 }}>
            {t('wasteDashboardAlertsTitle', 'Alerts and exceptions')}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('wasteDashboardAlertType', 'Type')}</TableCell>
                <TableCell>{t('wasteDashboardSite', 'Site')}</TableCell>
                <TableCell>{t('wasteDashboardDescription', 'Description')}</TableCell>
                <TableCell>{t('wasteDashboardRaisedDate', 'Raised')}</TableCell>
                <TableCell>{t('wasteDashboardSeverity', 'Severity')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedAlerts.map((alert) => (
                <TableRow key={alert.id}>
                  <TableCell>{alert.type}</TableCell>
                  <TableCell>{alert.site}</TableCell>
                  <TableCell>{alert.description}</TableCell>
                  <TableCell>{alert.raisedDate}</TableCell>
                  <TableCell><StatusChip status={alert.severity}
                    label={alert.severity === 'critical' ? t('wasteDashboardCritical', 'Critical') : t('wasteDashboardWarning', 'Warning')} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textMD"
            fontWeight={600}
            sx={{ mb: 3 }}>
            {t('wasteDashboardDataQualityTitle', 'Data quality')}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('wasteDashboardSite', 'Site')}</TableCell>
                <TableCell align="right">{t('wasteDashboardSubmissionRate', 'Submission rate')}</TableCell>
                <TableCell align="right">{t('wasteDashboardMissingSubmissions', 'Missing submissions')}</TableCell>
                <TableCell align="right">{t('wasteDashboardUnclassifiedWaste', 'Unclassified waste')}</TableCell>
                <TableCell align="right">{t('wasteDashboardEstimatedVsMeasured', 'Estimated vs measured')}</TableCell>
                <TableCell align="right">{t('wasteDashboardInconsistentUnits', 'Inconsistent units')}</TableCell>
                <TableCell align="right">{t('wasteDashboardDuplicateEntries', 'Duplicate entries')}</TableCell>
                <TableCell align="right">{t('wasteDashboardConfidenceScore', 'Confidence score')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {DATA_QUALITY.map((row) => (
                <TableRow key={row.site}>
                  <TableCell>{row.site}</TableCell>
                  <TableCell align="right">
                    <StatusChip status={row.submissionRatePercent === 100 ? 'good' : row.submissionRatePercent >= 90 ? 'warning' : 'critical'}
                      label={`${row.submissionRatePercent}%`} />
                  </TableCell>
                  <TableCell align="right">{row.missingSubmissions}</TableCell>
                  <TableCell align="right">{row.unclassifiedWastePercent}%</TableCell>
                  <TableCell align="right">{row.estimatedVsMeasuredPercent}%</TableCell>
                  <TableCell align="right">{row.inconsistentUnits}</TableCell>
                  <TableCell align="right">{row.duplicateEntries}</TableCell>
                  <TableCell align="right">
                    <StatusChip status={row.confidenceScore >= 85 ? 'good' : row.confidenceScore >= 70 ? 'warning' : 'critical'}
                      label={`${row.confidenceScore}/100`} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Section>
    </Box>
  );
};

export default AlertsDataQualityTab;
